import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiRefreshCw,
  FiSearch,
  FiX,
  FiCheckCircle,
  FiXCircle,
  FiMessageSquare,
  FiPaperclip,
  FiSave,
  FiSettings,
  FiInbox,
} from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import {
  adminSubscriptionPurchaseAPI,
  SubscriptionPlanConfig,
  SubscriptionPurchaseRequest,
} from '../services/api';
import { getSubscriptionPurchaseAttachmentUrl } from '../config/api';
import { notifySubscriptionPurchaseTicketsUpdated } from '../utils/adminSubscriptionPurchaseEvents';
import './MockCreditManagement.css';
import './MockCreditPanel.css';
import './MaterialManagement.css';
import '../pages/SubscriptionPurchase.css';
import './Dashboard.css';

interface SubscriptionPurchaseManagementProps {
  onLogout?: () => void;
  userEmail?: string;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso || '—';
  }
}

function formatIrr(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

const SubscriptionPurchaseManagement: React.FC<SubscriptionPurchaseManagementProps> = ({
  onLogout,
  userEmail,
}) => {
  const { isCollapsed } = useSidebar();
  const [activeTab, setActiveTab] = useState<'requests' | 'pricing'>('requests');
  const [config, setConfig] = useState<SubscriptionPlanConfig | null>(null);
  const [configForm, setConfigForm] = useState({
    price_usd: '',
    price_irr: '',
    duration_days: '30',
    card_to_card_instructions: '',
    payment_card_to_card_enabled: true,
    payment_direct_rial_enabled: false,
    payment_direct_usd_enabled: false,
    payment_direct_crypto_enabled: false,
  });
  const [requests, setRequests] = useState<SubscriptionPurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('open');
  const [awaitingFilter, setAwaitingFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SubscriptionPurchaseRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [replyFile, setReplyFile] = useState<File | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showApproveForm, setShowApproveForm] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const data = await adminSubscriptionPurchaseAPI.getConfig();
      setConfig(data);
      setConfigForm({
        price_usd: data.price_usd,
        price_irr: String(data.price_irr),
        duration_days: String(data.duration_days),
        card_to_card_instructions: data.card_to_card_instructions || '',
        payment_card_to_card_enabled: data.payment_card_to_card_enabled ?? true,
        payment_direct_rial_enabled: false,
        payment_direct_usd_enabled: false,
        payment_direct_crypto_enabled: false,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load pricing config');
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminSubscriptionPurchaseAPI.listRequests({
        status: statusFilter,
        awaiting_user: awaitingFilter || undefined,
        search: search.trim() || undefined,
      });
      setRequests(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, awaitingFilter, search]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (activeTab === 'requests') fetchRequests();
  }, [activeTab, fetchRequests]);

  const saveConfig = async () => {
    try {
      setConfigSaving(true);
      setError(null);
      const updated = await adminSubscriptionPurchaseAPI.updateConfig({
        price_usd: configForm.price_usd,
        price_irr: Number(configForm.price_irr),
        duration_days: Number(configForm.duration_days),
        card_to_card_instructions: configForm.card_to_card_instructions,
        payment_card_to_card_enabled: configForm.payment_card_to_card_enabled,
        payment_direct_rial_enabled: false,
        payment_direct_usd_enabled: false,
        payment_direct_crypto_enabled: false,
      });
      setConfig(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save pricing');
    } finally {
      setConfigSaving(false);
    }
  };

  const openDetail = async (id: number) => {
    try {
      setError(null);
      const detail = await adminSubscriptionPurchaseAPI.getRequest(id);
      setSelected(detail);
      setResolutionNote('');
      setRejectReason('');
      setReplyBody('');
      setReplyFile(null);
      setShowApproveForm(false);
      setShowRejectForm(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    }
  };

  const handleReply = async () => {
    if (!selected) return;
    if (!replyBody.trim() && !replyFile) {
      setError('Enter a message or attach a file');
      return;
    }
    try {
      setSubmitting(true);
      const updated = await adminSubscriptionPurchaseAPI.reply(selected.id, replyBody, replyFile);
      setSelected(updated);
      setReplyBody('');
      setReplyFile(null);
      await fetchRequests();
      notifySubscriptionPurchaseTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!selected) return;
    try {
      setSubmitting(true);
      const updated = await adminSubscriptionPurchaseAPI.approve(selected.id, {
        admin_resolution_note: resolutionNote,
        body: resolutionNote,
      });
      setSelected(updated);
      setShowApproveForm(false);
      await fetchRequests();
      notifySubscriptionPurchaseTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selected || !rejectReason.trim()) {
      setError('Rejection reason is required');
      return;
    }
    try {
      setSubmitting(true);
      const updated = await adminSubscriptionPurchaseAPI.reject(selected.id, rejectReason, rejectReason);
      setSelected(updated);
      setShowRejectForm(false);
      await fetchRequests();
      notifySubscriptionPurchaseTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  const renderAttachment = (reqId: number, att: { id: number; file_name: string; mime_type: string }) => {
    const url = getSubscriptionPurchaseAttachmentUrl(reqId, att.id);
    const isImage = att.mime_type.startsWith('image/');
    return (
      <div key={att.id} className="mock-credits-attachments">
        {url && isImage ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt={att.file_name} className="mock-credits-attachment-preview" />
          </a>
        ) : null}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="mock-credits-attachment-link">
            {React.createElement(FiPaperclip as React.ComponentType<{ size?: number }>, { size: 14 })}
            {att.file_name}
          </a>
        ) : null}
      </div>
    );
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />
      <div className="dashboard-main">
        <DashboardHeader userEmail={userEmail || 'admin'} organization="NAATI Admin" onLogout={onLogout} />
        <div className="dashboard-content">
          <div className="dashboard-content-inner">
            <motion.div
              className="dashboard-welcome"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <div>
                <h1 className="dashboard-title">Subscription purchase requests</h1>
                <p className="dashboard-subtitle">
                  Manage subscription purchase tickets, set pricing in USD and IRR, and approve or reject receipts.
                </p>
              </div>
            </motion.div>

            <div className="mock-credit-admin">
            <div className="material-tabs">
              <button
                type="button"
                className={`tab-button ${activeTab === 'requests' ? 'active' : ''}`}
                onClick={() => setActiveTab('requests')}
              >
                {React.createElement(FiInbox as React.ComponentType<{ size?: number }>, { size: 18 })}
                Purchase tickets
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'pricing' ? 'active' : ''}`}
                onClick={() => setActiveTab('pricing')}
              >
                {React.createElement(FiSettings as React.ComponentType<{ size?: number }>, { size: 18 })}
                Pricing & payment methods
              </button>
            </div>

            {error ? <div className="mock-credits-alert mock-credits-alert-warn">{error}</div> : null}

            {activeTab === 'pricing' ? (
              <div className="sub-purchase-pricing-form">
                <div>
                  <label htmlFor="price_usd">Price (USD)</label>
                  <input
                    id="price_usd"
                    type="number"
                    step="0.01"
                    min="0"
                    value={configForm.price_usd}
                    onChange={(e) => setConfigForm((f) => ({ ...f, price_usd: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="price_irr">Price (IRR)</label>
                  <input
                    id="price_irr"
                    type="number"
                    min="0"
                    value={configForm.price_irr}
                    onChange={(e) => setConfigForm((f) => ({ ...f, price_irr: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="duration_days">Duration (days)</label>
                  <input
                    id="duration_days"
                    type="number"
                    min="1"
                    max="3650"
                    value={configForm.duration_days}
                    onChange={(e) => setConfigForm((f) => ({ ...f, duration_days: e.target.value }))}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="card_instructions">Card-to-card instructions</label>
                  <textarea
                    id="card_instructions"
                    value={configForm.card_to_card_instructions}
                    onChange={(e) => setConfigForm((f) => ({ ...f, card_to_card_instructions: e.target.value }))}
                    placeholder="Bank name, card number, account holder…"
                  />
                </div>
                <div className="sub-purchase-payment-toggles">
                  <label className="sub-purchase-toggle-row">
                    <input
                      type="checkbox"
                      checked={configForm.payment_card_to_card_enabled}
                      onChange={(e) =>
                        setConfigForm((f) => ({ ...f, payment_card_to_card_enabled: e.target.checked }))
                      }
                    />
                    Card to card
                  </label>
                  <label className="sub-purchase-toggle-row sub-purchase-toggle-row--disabled">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled
                      readOnly
                    />
                    Direct Rial <span className="sub-purchase-toggle-soon">(Coming soon)</span>
                  </label>
                  <label className="sub-purchase-toggle-row sub-purchase-toggle-row--disabled">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled
                      readOnly
                    />
                    Direct USD <span className="sub-purchase-toggle-soon">(Coming soon)</span>
                  </label>
                  <label className="sub-purchase-toggle-row sub-purchase-toggle-row--disabled">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled
                      readOnly
                    />
                    Direct Crypto <span className="sub-purchase-toggle-soon">(Coming soon)</span>
                  </label>
                </div>
                {config ? (
                  <p style={{ gridColumn: '1 / -1', color: '#888', fontSize: '0.85rem' }}>
                    Current: ${Number(config.price_usd).toFixed(2)} · {formatIrr(config.price_irr)} IRR ·{' '}
                    {config.duration_days} days
                  </p>
                ) : null}
                <button
                  type="button"
                  className="mock-credits-btn mock-credits-btn-primary sub-purchase-btn-primary"
                  onClick={saveConfig}
                  disabled={configSaving}
                  style={{ gridColumn: '1 / -1', width: 'fit-content' }}
                >
                  {React.createElement(FiSave as React.ComponentType<{ size?: number }>, { size: 16 })}
                  {configSaving ? ' Saving…' : ' Save pricing'}
                </button>
              </div>
            ) : (
              <>
                <div className="mock-credit-admin-toolbar">
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="open">Open</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="all">All</option>
                  </select>
                  {statusFilter === 'open' ? (
                    <select value={awaitingFilter} onChange={(e) => setAwaitingFilter(e.target.value)}>
                      <option value="">All open</option>
                      <option value="0">Awaiting admin</option>
                      <option value="1">Awaiting user</option>
                    </select>
                  ) : null}
                  <input
                    type="search"
                    className="mock-credit-admin-search"
                    placeholder="Search email, phone…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchRequests()}
                  />
                  <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={fetchRequests}>
                    {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 16 })}
                    Search
                  </button>
                  <button
                    type="button"
                    className="mock-credits-btn mock-credits-btn-secondary"
                    onClick={fetchRequests}
                    disabled={loading}
                  >
                    {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, {
                      size: 16,
                      className: loading ? 'spinner' : undefined,
                    })}
                  </button>
                </div>

                <div className="mock-credit-admin-table-wrap">
                  {loading ? (
                    <p className="mock-credits-tickets-empty">Loading…</p>
                  ) : requests.length === 0 ? (
                    <p className="mock-credits-tickets-empty">No requests found.</p>
                  ) : (
                    <table className="mock-credit-admin-table">
                      <thead>
                        <tr>
                          <th>Ticket</th>
                          <th>User</th>
                          <th>Method</th>
                          <th>Amount</th>
                          <th>Contact</th>
                          <th>Status</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map((req) => (
                          <tr key={req.id} onClick={() => openDetail(req.id)}>
                            <td>#{req.id}</td>
                            <td>{req.user_email}</td>
                            <td>{req.payment_method_label}</td>
                            <td>
                              ${req.price_usd_snapshot} / {formatIrr(req.price_irr_snapshot)} IRR
                            </td>
                            <td>{req.contact_phone || req.contact_email || '—'}</td>
                            <td>
                              <span className={`mock-credits-status mock-credits-status-${req.status}`}>
                                {req.status === 'open' && req.awaiting_user ? 'Awaiting user' : req.status}
                              </span>
                            </td>
                            <td>{formatDate(req.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {selected ? (
          <motion.div
            className="mock-credits-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && setSelected(null)}
          >
            <motion.div
              className="mock-credits-modal mock-credits-modal-wide"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mock-credits-modal-header">
                <h3>
                  {React.createElement(FiMessageSquare as React.ComponentType<{ size?: number }>, { size: 20 })}
                  {' '}
                  Ticket #{selected.id} — {selected.user_email}
                </h3>
                <button type="button" className="mock-credits-modal-close" onClick={() => setSelected(null)}>
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
                </button>
              </div>
              <div className="mock-credits-modal-body">
                <p style={{ color: '#aaa', fontSize: '0.85rem' }}>
                  {selected.payment_method_label} · ${selected.price_usd_snapshot} /{' '}
                  {formatIrr(selected.price_irr_snapshot)} IRR · {selected.duration_days_snapshot} days · Phone:{' '}
                  {selected.contact_phone || '—'} · Email: {selected.contact_email || '—'}
                </p>
                <div className="mock-credits-thread">
                  {(selected.messages || []).map((msg) => (
                    <div
                      key={msg.id}
                      className={`mock-credits-msg mock-credits-msg-${msg.author_role === 'admin' ? 'admin' : 'user'}`}
                    >
                      <div className="mock-credits-msg-role">{msg.author_role === 'admin' ? 'Admin' : 'User'}</div>
                      <div className="mock-credits-msg-body">{msg.body}</div>
                      <div className="mock-credits-msg-time">{formatDate(msg.created_at)}</div>
                      {msg.attachments?.map((att) => renderAttachment(selected.id, att))}
                    </div>
                  ))}
                  {(selected.attachments || [])
                    .filter(
                      (att) =>
                        !(selected.messages || []).some((m) => m.attachments?.some((a) => a.id === att.id))
                    )
                    .map((att) => renderAttachment(selected.id, att))}
                </div>

                {selected.status === 'open' ? (
                  <>
                    <div className="mock-credits-reply-box">
                      <div className="mock-credits-form-group">
                        <label>Reply to user</label>
                        <textarea
                          value={replyBody}
                          onChange={(e) => setReplyBody(e.target.value)}
                          placeholder="e.g. Please confirm transaction reference…"
                        />
                      </div>
                      <input type="file" accept="image/*,.pdf" onChange={(e) => setReplyFile(e.target.files?.[0] ?? null)} />
                    </div>

                    <div className="mock-credit-admin-actions">
                      <button
                        type="button"
                        className="mock-credits-btn mock-credits-btn-secondary"
                        onClick={handleReply}
                        disabled={submitting}
                      >
                        Send reply (await user)
                      </button>
                      <button
                        type="button"
                        className="mock-credits-btn mock-credits-btn-primary sub-purchase-btn-primary"
                        onClick={() => {
                          setShowApproveForm(!showApproveForm);
                          setShowRejectForm(false);
                        }}
                      >
                        {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 16 })}
                        Approve & grant subscription
                      </button>
                      <button
                        type="button"
                        className="mock-credits-btn mock-credits-btn-secondary"
                        onClick={() => {
                          setShowRejectForm(!showRejectForm);
                          setShowApproveForm(false);
                        }}
                      >
                        {React.createElement(FiXCircle as React.ComponentType<{ size?: number }>, { size: 16 })}
                        Reject
                      </button>
                    </div>

                    {showApproveForm ? (
                      <div className="mock-credit-admin-approve-form">
                        <label>Note to user</label>
                        <textarea
                          value={resolutionNote}
                          onChange={(e) => setResolutionNote(e.target.value)}
                          placeholder="Payment confirmed. Your subscription is now active."
                        />
                        <span />
                        <button
                          type="button"
                          className="mock-credits-btn mock-credits-btn-primary sub-purchase-btn-primary"
                          onClick={handleApprove}
                          disabled={submitting}
                        >
                          Confirm approval ({selected.duration_days_snapshot} days)
                        </button>
                      </div>
                    ) : null}

                    {showRejectForm ? (
                      <div className="mock-credit-admin-approve-form">
                        <label>Rejection reason</label>
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Required — shown to user"
                        />
                        <span />
                        <button
                          type="button"
                          className="mock-credits-btn mock-credits-btn-secondary"
                          onClick={handleReject}
                          disabled={submitting}
                        >
                          Confirm rejection
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="mock-credits-alert mock-credits-alert-info">
                    Closed — {selected.status}
                    {selected.granted_subscription
                      ? ` · Expires ${formatDate(selected.granted_subscription.expires_at)}`
                      : ''}
                    {selected.admin_resolution_note ? `: ${selected.admin_resolution_note}` : ''}
                  </div>
                )}
              </div>
              <div className="mock-credits-modal-footer">
                <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={() => setSelected(null)}>
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionPurchaseManagement;
